import * as recipeListService from "../../services/recipeList/recipeListService";
import * as customGroupService from "../../services/recipeList/customGroupService";
import { queryClient } from "../../services/utils";

const state = {
 recipeLists: [],
 selectedRecipe: null,
 totalRecipeLists: 0,
 fftCountries: {},
};

const mutations = {
 SET_RECIPE_LISTS(state, { recipeLists }) {
  state.recipeLists = recipeLists;
 },

 SET_SELECTED_RECIPE(state, selectedRecipe) {
  state.selectedRecipe = selectedRecipe == null ? null : { ...selectedRecipe };
 },

 SET_RECIPE_CHANGED(state, value) {
  state.selectedRecipe = { ...state.selectedRecipe, isRecipeChanged: value };
 },

 REMOVE_RECIPE_INGREDIENT(state, ingredientIndex) {
  state.selectedRecipe.recipe_list_ingredients.splice(ingredientIndex, 1);
 },

 REMOVE_RECIPE_GROUP(state, groupIndex) {
  state.selectedRecipe.groups.splice(groupIndex, 1);
 },

 SET_TOTAL_RECIPE_LISTS(state, totalRecipeLists) {
  state.totalRecipeLists = totalRecipeLists;
 },

 SET_FFT_COUNTRIES(state, fftCountries) {
  state.fftCountries = fftCountries;
 },
};

const actions = {
 resetCache({ dispatch }) {
  queryClient.removeQueries(["FETCH_RECIPE_LISTS"], { exact: false });
  dispatch("fftList/clearCachedFftData", null, { root: true });
 },

 async fetchRecipeLists({ commit, state }, options = {}) {
  const { page = 1, limit = 10, query = "", includeIngredients = false } = options;

  // check the cache first
  const cachedData = queryClient.getQueryData(["FETCH_RECIPE_LISTS", { page, limit, query, includeIngredients }], { active: true });

  if (cachedData) {
   commit("SET_RECIPE_LISTS", { recipeLists: cachedData?.data?.data });
   commit("SET_TOTAL_RECIPE_LISTS", cachedData?.data?.total);

   return cachedData;
  }

  try {
   const response = await queryClient.fetchQuery(
    ["FETCH_RECIPE_LISTS", { page, limit, query, includeIngredients }],
    recipeListService.fetchRecipeLists,
    {
     refetchOnMount: false,
    },
   );

   const recipeLists = response.data.data;
   const total = response.data.total;

   commit("SET_RECIPE_LISTS", { recipeLists });
   commit("SET_TOTAL_RECIPE_LISTS", total);

   if (recipeLists.length && state.selectedRecipe) {
    const currentRecipeList = recipeLists.find(recipeList => recipeList.id === state.selectedRecipe.id);
    commit("SET_SELECTED_RECIPE", currentRecipeList);
   }

   return response;
  } catch (error) {
   console.error(error);
  }
 },

 updateRecipeInRecipeLists({ commit, dispatch }, recipe) {
  const recipeLists = [...state.recipeLists];
  const recipeListIndex = recipeLists.findIndex(recipeList => recipeList.id === recipe.id);
  recipeLists[recipeListIndex] = recipe;

  commit("SET_RECIPE_LISTS", { recipeLists });
  if (state.selectedRecipe && state.selectedRecipe.id === recipe.id) {
   commit("SET_SELECTED_RECIPE", recipe);
  }
 },

 async fetchRecipeListWithNutrSpecs({ dispatch }, recipeListId) {
  try {
   const response = await recipeListService.fetchRecipeListWithNutrSpecs(recipeListId);
   if (response.status === 200) {
    return response.data;
   } else {
   }
  } catch (error) {
   console.error(error);
  }
 },
 async fetchRecipeList({ dispatch }, payload) {
  let includeIngredients = true;
  let recipeListId = payload;
  if (typeof payload === "object") {
   recipeListId = payload.recipeListId;
   includeIngredients = payload.includeIngredients;
  }
  try {
   const response = await recipeListService.fetchRecipeList(recipeListId, includeIngredients);

   if (response.status === 200) {
    const recipeList = response.data.data;
    dispatch("updateRecipeInRecipeLists", recipeList);
   }

   return response;
  } catch (error) {
   console.error(error);
  }
 },

 async createRecipeList({ commit, dispatch }, requestBody) {
  try {
   const response = await recipeListService.createRecipeList(requestBody);

   if (response.status === 200) {
    let last_pinned_index = state.recipeLists.findLastIndex(x => x.is_pinned);
    const recipeLists = state.recipeLists.toSpliced(last_pinned_index + 1, 0, response.data.data);
    commit("SET_RECIPE_LISTS", { recipeLists });
    commit("SET_TOTAL_RECIPE_LISTS", state.totalRecipeLists + 1);

    dispatch("resetCache");
   }
   return response;
  } catch (e) {
   console.error(e);
  }
 },

 async createRecipeListCopy({ commit, dispatch }, { recipeListId, requestBody }) {
  try {
   const response = await recipeListService.createRecipeListCopy(recipeListId, requestBody);

   if (response.status === 200) {
    let last_pinned_index = state.recipeLists.findLastIndex(x => x.is_pinned);
    const recipeLists = state.recipeLists.toSpliced(last_pinned_index + 1, 0, response.data.data);
    commit("SET_RECIPE_LISTS", { recipeLists });
    commit("SET_TOTAL_RECIPE_LISTS", state.totalRecipeLists + 1);

    dispatch("resetCache");
   }
   return response;
  } catch (e) {
   console.error(e);
  }
 },

 async updateRecipeList({ commit, dispatch }, { recipeListId, requestBody }) {
  try {
   const response = await recipeListService.updateRecipeList(recipeListId, requestBody);

   if (response.status === 200) {
    const updatedRecipeList = response.data.data;
    dispatch("updateRecipeInRecipeLists", updatedRecipeList);
    dispatch("resetCache");
   }

   return response;
  } catch (e) {
   console.error(e);
  }
 },

 async updateRecipeListIngredients({ commit, dispatch }, { recipeListId, requestBody }) {
  try {
   const response = await recipeListService.updateRecipeListIngredients(recipeListId, requestBody);

   if (response.status === 200) {
    dispatch("resetCache");

    const updatedRecipeList = response.data.data;
    dispatch("updateRecipeInRecipeLists", updatedRecipeList);
   }
   return response;
  } catch (e) {
   console.error(e);
  }
 },

 updateSelectedRecipeList({ commit }, recipe) {
  commit("SET_SELECTED_RECIPE", recipe);
 },

 async getLatestIngredientsPrices({}) {
  try {
   const response = await recipeListService.getLatestIngredientsPrices(state.selectedRecipe.id);
   return response?.data?.data;
  } catch (e) {
   console.error(e);
  }
 },

 isSelectedRecipeChanged({ commit, dispatch }, value) {
  commit("SET_RECIPE_CHANGED", value);
 },

 removeRecipeIngredient({ commit, dispatch }, { ingredientRowId }) {
  commit("REMOVE_RECIPE_INGREDIENT", ingredientRowId);
  dispatch("isSelectedRecipeChanged", true);
  dispatch("resetCache");
 },

 removeRecipeGroup({ commit, dispatch }, groupRowId) {
  commit("REMOVE_RECIPE_GROUP", groupRowId);
  dispatch("isSelectedRecipeChanged", true);
 },

 async removeRecipeList({ dispatch, commit }, recipeListId) {
  const response = await recipeListService.deleteRecipeList(recipeListId);
  if (response.status === 200) {
   // remove it from the recipeLists array
   const recipeLists = [...state.recipeLists];
   const recipeListIndex = recipeLists.findIndex(recipeList => recipeList.id === recipeListId);
   recipeLists.splice(recipeListIndex, 1);
   commit("SET_RECIPE_LISTS", { recipeLists });
   commit("SET_TOTAL_RECIPE_LISTS", state.totalRecipeLists - 1);
   dispatch("resetCache");
  }

  return response;
 },

 // update all recipe groups that are changed
 async updateRecipeGroups({ commit, dispatch }, { recipeListId, requestBody }) {
  try {
   const response = await recipeListService.updateRecipeGroups(recipeListId, requestBody);

   if (response.status === 200) {
    dispatch("resetCache");

    const updatedRecipeList = response.data.data;
    dispatch("updateRecipeInRecipeLists", updatedRecipeList);
   }
   return response;
  } catch (e) {
   console.error(e);
  }
 },

 /**
  *
  * Actions for managing custom  groups in ingredient lists
  *
  */
 async createCustomGroup({ commit, dispatch }, requestBody) {
  const response = await customGroupService.createCustomGroup(requestBody);
  if (response.status === 200) {
   const updatedRecipeList = { ...state.selectedRecipe };
   const customGroup = response.data.data;
   updatedRecipeList.groups.unshift({
    ...customGroup,
    is_custom: true,
    is_visible: true,
   });

   dispatch("updateSelectedRecipeList", updatedRecipeList);
   dispatch("resetCache");
  }
  return response;
 },

 async updateCustomGroup({ commit, dispatch }, { customGroupId, requestBody }) {
  const response = await customGroupService.updateCustomGroup(customGroupId, requestBody);
  if (response.status === 200) {
   const updatedRecipeList = { ...state.selectedRecipe };
   const customGroupIndex = updatedRecipeList.groups.findIndex(group => group.id === customGroupId);

   updatedRecipeList.groups[customGroupIndex] = {
    ...response.data,
    is_custom: true,
    is_visible: true,
   };

   dispatch("updateSelectedRecipeList", updatedRecipeList);
   dispatch("resetCache");
  }
  return response;
 },

 async deleteCustomGroup({ commit, dispatch }, { customGroupId }) {
  const response = await customGroupService.deleteCustomGroup(customGroupId);
  if (response.status === 200) {
   const updatedRecipeList = { ...state.selectedRecipe };
   const customGroupIndex = updatedRecipeList.groups.findIndex(group => group.id === customGroupId);
   updatedRecipeList.groups.splice(customGroupIndex, 1);
   dispatch("updateSelectedRecipeList", updatedRecipeList);
   dispatch("resetCache");
  }
  return response;
 },

 async fetchFftCountries({ commit, dispatch }) {
  const response = await recipeListService.fetchCountries();

  if (response.status === 200) {
   const countriesMap = response.data.reduce((acc, country) => {
    acc[country.country_id] = country;
    return acc;
   }, {});
   console.log(countriesMap)
   commit("SET_FFT_COUNTRIES", countriesMap);
  }
  return response;
 },
};

const getters = {
 getRecipeLists: state => state.recipeLists,
 getSelectedRecipe: state => state.selectedRecipe,
 getTotalRecipeLists: state => state.totalRecipeLists,
 getFftCountries: state => state.fftCountries,
};

export default {
 namespaced: true,
 state,
 getters,
 actions,
 mutations,
};
